import styled, { css } from 'styled-components';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { media } from 'ui';
import { useBooking } from 'hooks';
import { Stack } from '@koob/margaret';
import {
  toOptionalSupplementOptions,
  resolveSupplementOnBed,
} from '@koob/utils';
import {
  PromotionTag,
  RequiredPromotionTag,
  KoobButton,
  KoobInputSelect,
} from '@koob/ui';

const RoomExtraContent = styled(Stack)`
  padding-left: 0;
  padding-right: ${({ theme }) => theme.spacing()};

  ${media.tablet`
    padding-left: ${({ theme }) => theme.spacing()};
  `}
  p {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
    margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  }
`;

export const Promotion = styled(Stack).attrs({
  alignY: 'center',
  gutterSize: 0.25,
  marginBottom: 0.5,
})`
  font-size: 0.75rem;
  padding: ${({ theme }) => theme.spacing(0.25)}
    ${({ theme }) => theme.spacing(0.75)};
  border-radius: 40px;
  color: #2e1065;
  background-color: ${({ bgColor }) => bgColor ?? '#e9d5ff'};

  ${props =>
    props.promo &&
    css`
      padding: ${({ theme }) => theme.spacing(0.2)};
      margin-top: -1px !important;
    `}
`;

export const BedTag = styled(Stack).attrs({
  gutterSize: 0.5,
  alignY: 'center',
})`
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.separator};
  padding: 3px 10px;
  ${props =>
    props.selected &&
    css`
      background: ${({ theme }) => theme.orangeLight2};
      color: ${({ theme }) => theme.orange};
    `}
  ${props =>
    props.red &&
    css`
      background-color: #ffe3e3;
      color: #ea5d5d !important;
    `}
    ${props =>
    props.grey &&
    css`
      color: rgba(0, 0, 0, 0.5);
      background-color: #eeeeee;
    `}
    ${props =>
    props.green &&
    css`
      background: rgba(115, 201, 146, 0.12);
      color: #73c992;
    `}

    border-radius: 5px;
`;

const Separator = styled.div`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.separator};
  margin-top: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

export function RoomCard({
  possibility,
  currentBed,
  currentBedIndex,
  changeSelectedBed,
  isSelected,
  isRoomSelected,
  deleteRoom,
  promotions,
  selectedRoomsFromPromotionSelect,
  setSelectedPromotionSupplements,
  selectedPromotionSupplements,
  supplementKey,
  updateRoomSupplements,
}) {
  const {
    selectedSupplements,
    setSelectedSupplements,
    currentSelectedRoom,
    setCurrentSelectedRoom,
    selectedRooms,
  } = useBooking();
  const { t } = useTranslation('booking', { keyPrefix: 'room' });

  const [values, setValues] = useState();
  const [selectedPromotion, setSelectedPromotion] = useState(0);
  const [clicked, setClicked] = useState(false);

  const selectedRoomsCount = selectedRooms.length ?? 0;
  let promotionIndex = 0;
  const promotionOptions =
    possibility.allPromotions
      ?.flatMap(promotionCombinations => {
        return Object.entries(promotionCombinations).map(
          ([key, promotionCombination]) => {
            if (currentBed && promotionCombination.length) {
              const result = {
                label: promotionCombination.join(', '),
                value: key,
                index: promotionIndex,
                promotions: promotionCombination,
              };
              promotionIndex++;
              return result;
            } else {
              return null;
            }
          },
        );
      })
      .filter(item => item !== null) ?? [];

  const uniquePromotionOptions = Array.from(
    new Map(promotionOptions.map(item => [item.label, item])).values(),
  );

  const promotionOptionsFiltered = (uniquePromotionOptions || []).filter(
    item => !promotions.some(sup => sup.value === item.label),
  );

  const koediaPromos = currentBed?.promotions?.filter(item => item !== null);

  // The options are built from the supplements of the bed being shown, not
  // looked up by name in the booking-wide `promotions` list: that list is
  // deduplicated by name across every room, so a percentage supplement quoted
  // its FIRST room's amount — and its id — on every other room.
  const filteredOptionSupplement = uniqBy(
    toOptionalSupplementOptions(
      currentBed?.optionalSupplements ??
        possibility?.bedChoices?.[currentBedIndex]?.optionalSupplements,
    ),
    'displayName',
  );

  const handleClick = e => {
    if (clicked) {
      e.stopPropagation();
    } else {
      setClicked(true);
    }
  };

  // Replaces react-select's onMenuOpen: syncs the room selection when the
  // user interacts with a select on a not-yet-selected room card.
  const handleMenuOpen = () => {
    if (!isSelected && selectedRoomsFromPromotionSelect) {
      setSelectedPromotion(
        Math.max(
          0,
          promotionOptionsFiltered.findIndex(p => p.index === values?.index),
        ),
      );
      selectedRoomsFromPromotionSelect(
        possibility.originalIndex,
        values?.label,
        currentBed?.name,
      );
    }
  };

  const renderPromotions = () => {
    const filteredPromotions = isRoomSelected
      ? currentBed?.promotions
      : promotionOptionsFiltered[selectedPromotion]?.promotions;

    const supplements =
      currentBed?.totalPriceWithoutPromo !== currentBed?.totalPriceWithPromo
        ? currentBed?.mandatorySupplements
        : [];

    if (currentBed?.promotions?.length > 0 || supplements?.length > 0) {
      return (
        <RoomExtraContent size="full" direction="column" gutterSize={0.5}>
          <Separator />

          <Stack
            style={{
              flexWrap: 'wrap',
              gap: '0.5rem',
            }}
          >
            {filteredPromotions?.map((name, index) => (
              <PromotionTag key={index} name={name} />
            ))}

            {supplements?.map((name, index) => (
              <RequiredPromotionTag
                label={t('requiredSupplement', { keyPrefix: '' })}
                key={index}
                name={name}
              />
            ))}
          </Stack>
        </RoomExtraContent>
      );
    }
  };

  useEffect(() => {
    if (isSelected && selectedPromotion !== 0) {
      setSelectedPromotion(
        Math.max(
          0,
          promotionOptionsFiltered.findIndex(p => p.index === values?.index),
        ),
      );
      setClicked(false);
    }
    if (isSelected && selectedRoomsFromPromotionSelect) {
      selectedRoomsFromPromotionSelect(
        possibility.originalIndex,
        values?.label,
        currentBed?.name,
      );
    }
  }, [currentBedIndex, isSelected]);

  useEffect(() => {
    setSelectedPromotion(0);
  }, [isSelected]);

  useEffect(() => {
    if (!isSelected && selectedRoomsFromPromotionSelect) {
      setClicked(false);
      setSelectedPromotion(
        Math.max(
          0,
          promotionOptionsFiltered.findIndex(p => p.index === values?.index),
        ),
      );
      selectedRoomsFromPromotionSelect(
        possibility.originalIndex,
        values?.label,
        currentBed?.name,
      );
    }
  }, [selectedPromotion, values]);

  useEffect(() => {
    // `currentSelectedRoom` guards the spread below: with no room being chosen
    // it builds one out of nothing — a truthy `{ bed: { …: NaN } }` that then
    // counts as a pending room in the recap and in the extra-nights note.
    //
    // `selectedSupplements` is booking-wide, so every card reacts to it. Only
    // the card being chosen may fold it into `currentSelectedRoom`: any other
    // card re-prices that room off ITS OWN `currentBedIndex`, silently moving
    // the total back to a bed the user did not pick. A validated room is
    // excluded too — its supplements go through updateRoomSupplements.
    if (
      !selectedPromotion &&
      currentSelectedRoom &&
      isSelected &&
      !isRoomSelected
    ) {
      // A selection survives a bed change but carries the previous bed's ids
      // and amount, so it is re-read off the bed being booked before it is
      // priced in and stored on the room.
      const bedSupplements = (selectedSupplements ?? []).map(supplement =>
        resolveSupplementOnBed(filteredOptionSupplement, supplement),
      );

      const promosPrice = bedSupplements.reduce(
        (total, { price }) => total + (price ?? 0),
        0,
      );

      // Re-price from the bed the room actually holds, not from this card's
      // `currentBedIndex`: the two part company when the card was left on
      // another bed configuration and picked again, and the price would then
      // quote a bed that is not the one being booked. `currentBedIndex` is the
      // fallback for a bed the possibility does not list by id.
      const baseBed =
        currentSelectedRoom?.bedChoices?.find(
          bedChoice => bedChoice?.id === currentSelectedRoom?.bed?.id,
        ) ?? currentSelectedRoom?.bedChoices?.[currentBedIndex];

      setCurrentSelectedRoom({
        ...currentSelectedRoom,
        bed: {
          ...currentSelectedRoom?.bed,
          totalPriceWithPromo: baseBed?.totalPriceWithPromo + promosPrice,
        },
        optionalPromos: bedSupplements,
      });
    }
    // `currentBedIndex` too: picking another bed configuration rebuilds
    // `currentSelectedRoom` from the raw possibility, which carries no
    // `optionalPromos`. Without re-running here the supplements stayed ticked in
    // the select but were gone from the room — off the price, off the payload,
    // and off the extra-nights note.
  }, [selectedSupplements, currentBedIndex]);

  // The supplements currently ticked on THIS room. It has to be read from the
  // same place onValueChange writes (`selectedPromotionSupplements`, keyed per
  // room), otherwise the select is a controlled input whose value never moves
  // and no option can be ticked. A validated room falls back to what it was
  // stored with, so reopening the page shows its real selection.
  const supplementStoreKey = supplementKey ?? possibility.originalIndex;
  const currentRoomSupplements = (
    selectedPromotionSupplements?.[supplementStoreKey] ??
    (isRoomSelected ? possibility.optionalPromos : []) ??
    []
  ).map(supplement =>
    // Ids are per bed: a selection ticked on another bed configuration has to
    // be re-read off this one, or the option no longer appears ticked.
    resolveSupplementOnBed(filteredOptionSupplement, supplement),
  );

  const selectedPromo = promotionOptionsFiltered?.find(item => {
    const selectedBedPromotions =
      selectedRooms?.[selectedRooms?.length - 1]?.bed?.promotions ?? [];
    if (selectedBedPromotions.length === 0) {
      return false;
    }
    return item.label === selectedBedPromotions.join(', ');
  });

  const roomName = `${possibility?.name?.toLowerCase().split(' ').join('-')}`;

  return (
    <div className="flex-col space-y-3">
      {Array.isArray(possibility.allPromotions) === true &&
        possibility.allPromotions.length > 1 &&
        promotionOptionsFiltered.length > 1 && (
          <div>
            <div className="mb-1.5 text-lg font-medium">
              <i className="fal fa-tag mr-1.5" />
              {t('choosePromotion')}
            </div>

            <div
              className="w-96"
              id={
                !isRoomSelected
                  ? `select-promotion-${selectedRooms.length}`
                  : undefined
              }
              onClick={event => {
                handleClick(event);
                handleMenuOpen();
              }}
            >
              {/* Stored promotion values stay full option objects
                  ({ label, value, index, promotions }); the select is keyed
                  by label since raw values can collide across combinations. */}
              <KoobInputSelect
                placeholder={'Select a promotions'}
                options={promotionOptionsFiltered.map(item => ({
                  value: item.label,
                  label: item.label,
                }))}
                value={
                  (isRoomSelected
                    ? (selectedPromo ?? promotionOptionsFiltered[0])
                    : (promotionOptionsFiltered[selectedPromotion] ??
                      promotionOptionsFiltered[0])
                  )?.label ?? ''
                }
                onValueChange={label => {
                  const value = promotionOptionsFiltered.find(
                    p => p.label === label,
                  );
                  if (!value) {
                    return;
                  }
                  setValues(value);
                  setSelectedPromotion(
                    Math.max(
                      0,
                      promotionOptionsFiltered.findIndex(p => p === value),
                    ),
                  );
                  if (selectedRoomsFromPromotionSelect) {
                    selectedRoomsFromPromotionSelect(
                      possibility.originalIndex,
                      value.label,
                      currentBed.name,
                    );
                  }
                }}
                disabled={isRoomSelected}
              />
            </div>
          </div>
        )}

      {Array.isArray(filteredOptionSupplement) === true &&
        filteredOptionSupplement?.length > 0 && (
          <div>
            <div className="mb-1.5 text-lg font-medium">
              <i className="fal fa-tag mr-1.5" />
              {t('chooseOptionalPromotion')}
            </div>

            <div
              className="w-96"
              id={
                !isRoomSelected
                  ? `select-supplement-${selectedRooms.length}`
                  : undefined
              }
              onClick={event => {
                handleClick(event);
                handleMenuOpen();
              }}
            >
              {/* Stored supplement values stay arrays of api objects
                  ({ id, displayName, price, ... }); ids are translated at
                  the select boundary. */}
              <KoobInputSelect
                key={possibility.originalIndex}
                placeholder={'Select supplements'}
                multiple
                options={filteredOptionSupplement.map(item => ({
                  value: item.id,
                  label: item.displayName,
                }))}
                value={currentRoomSupplements.map(item => item.id)}
                onValueChange={ids => {
                  const value = filteredOptionSupplement.filter(item =>
                    ids.includes(item.id),
                  );
                  setSelectedPromotionSupplements(prev => {
                    return {
                      ...prev,
                      [supplementStoreKey]: value,
                    };
                  });
                  if (isRoomSelected) {
                    // Already validated: `currentSelectedRoom` points elsewhere,
                    // so the change has to land on the stored room or it never
                    // reaches the booking payload.
                    updateRoomSupplements?.(value);
                  } else {
                    setSelectedSupplements(value);
                  }
                }}
              />
            </div>
          </div>
        )}

      {renderPromotions()}

      {possibility?.supplier === 'Koedia' && koediaPromos?.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {koediaPromos?.map((name, index) => (
            <PromotionTag key={index} name={name} />
          ))}
        </div>
      )}

      {(possibility.bedChoices ?? []).length > 1 && (
        <div>
          <div className="mb-1.5 text-lg font-medium">
            <i className="fal fa-bed mr-1.5" />
            {t('selectedComposition')}
          </div>

          <div className="flex flex-wrap gap-3">
            {(possibility?.bedChoices ?? []).map((bedElement, index) => (
              <BedTag
                key={index}
                onClick={e => {
                  e.stopPropagation();
                  if (currentBedIndex !== index) {
                    changeSelectedBed(index);
                  }
                }}
                selected={currentBed?.id === bedElement?.id}
              >
                <div
                  data-testid={
                    !isRoomSelected &&
                    `${bedElement.hasExtraBed ? 'extra_bed' : 'koob_bed'}_${roomName}_${selectedRoomsCount}`
                  }
                >
                  {bedElement.name}
                </div>
              </BedTag>
            ))}
          </div>
        </div>
      )}

      {isRoomSelected && (
        <Stack size="full" alignX="end">
          <KoobButton
            type="button"
            onClick={() => {
              deleteRoom();
            }}
          >
            <i className="far fa-pen mr-1.5" />
            {t('update')}
          </KoobButton>
        </Stack>
      )}
    </div>
  );
}
